import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ProfilePicture from './shared/ProfilePicture';
import {
  XMarkIcon,
  UserGroupIcon,
  UserIcon,
  BeakerIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface Participant {
  id: string;
  member_id: string;
  mg_nr?: string;
  vorname: string;
  nachname: string;
  name?: string;
  is_guest?: boolean;
  card_type?: string;
  issues?: Array<{
    hundename: string;
    problems: string[];
  }>;
  gebucht_am?: string;
}

interface ParticipantsModalProps {
  event: any;
  isOpen: boolean;
  onClose: () => void;
}

const ParticipantsModal: React.FC<ParticipantsModalProps> = ({ event, isOpen, onClose }) => {
  const { config } = useContext(AuthContext);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsVisible(true), 10);
      fetchParticipants();
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const fetchParticipants = async () => {
    if (!event?.id) return;

    setLoading(true);
    try {
      const response = await apiClient.get(`/api/calendar/${event.id}/participants`);
      if (response.data.success) {
        setParticipants(response.data.participants || []);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
      setError('Fehler beim Laden der Teilnehmer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  const getParticipantIssues = (participant: Participant) => {
    const tollwutIssues: string[] = [];
    const insuranceIssues: string[] = [];

    participant.issues?.forEach((issue) => {
      issue.problems.forEach((problem) => {
        if (problem.toLowerCase().includes('tollwut')) {
          tollwutIssues.push(`${issue.hundename}: ${problem}`);
        } else if (
          problem.toLowerCase().includes('versicherung') ||
          problem.toLowerCase().includes('haftpflicht')
        ) {
          insuranceIssues.push(`${issue.hundename}: ${problem}`);
        }
      });
    });

    return {
      hasTollwutIssue: tollwutIssues.length > 0,
      tollwutIssues,
      hasInsuranceIssue: insuranceIssues.length > 0,
      insuranceIssues,
    };
  };

  if (!isOpen) return null;

  const bookingCount = event?.extendedProps?.bookingCount || 0;
  const maxBookable = event?.extendedProps?.bookable || 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-9 h-9 rounded-xl bg-info-100 dark:bg-info-900/40 text-info-600 dark:text-info-400 grid place-items-center flex-shrink-0">
            <UserGroupIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Teilnehmer</h3>
              <span className="px-2 py-0.5 text-[10px] font-bold bg-info-100 dark:bg-info-900/50 text-info-700 dark:text-info-300 rounded-full flex-shrink-0">
                {bookingCount}/{maxBookable}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{event?.title}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-info-600 border-t-transparent rounded-full mb-4" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Teilnehmer werden geladen...</span>
              </div>
            ) : error ? (
              <div className="p-4 bg-error-50 dark:bg-error-900/20 rounded-xl border border-error-200 dark:border-error-700">
                <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
              </div>
            ) : participants.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                  <UserIcon className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-1">Keine Teilnehmer</p>
                <p className="text-sm text-gray-500 dark:text-gray-500">Fuer diesen Termin sind noch keine Buchungen vorhanden</p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Mitglieder Section */}
                {participants.filter(p => !p.is_guest).length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                      <UserIcon className="w-3.5 h-3.5" />
                      Mitglieder ({participants.filter(p => !p.is_guest).length})
                    </h4>
                    <div className="space-y-2">
                      {participants.filter(p => !p.is_guest).map((participant) => {
                        const { hasTollwutIssue, hasInsuranceIssue } = getParticipantIssues(participant);
                        const displayName = `${participant.vorname} ${participant.nachname || participant.name || ''}`;
                        const hasAnyIssue = hasTollwutIssue || hasInsuranceIssue;

                        return (
                          <div
                            key={participant.member_id || participant.id}
                            className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600"
                          >
                            <ProfilePicture participant={participant} size="lg" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{displayName}</p>
                              {participant.gebucht_am && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Gebucht: {new Date(participant.gebucht_am).toLocaleDateString('de-DE')}
                                </p>
                              )}
                              {hasAnyIssue && (
                                <div className="flex items-center gap-2 mt-1.5">
                                  {hasTollwutIssue && (
                                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-error-100 dark:bg-error-900/30 rounded">
                                      <BeakerIcon className="w-3 h-3 text-error-600" />
                                      <span className="text-xs font-medium text-error-700 dark:text-error-400">Impfung</span>
                                    </div>
                                  )}
                                  {hasInsuranceIssue && (
                                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-error-100 dark:bg-error-900/30 rounded">
                                      <ShieldCheckIcon className="w-3 h-3 text-error-600" />
                                      <span className="text-xs font-medium text-error-700 dark:text-error-400">Versicherung</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className={`p-2 rounded-full ${hasAnyIssue ? 'bg-error-100 dark:bg-error-900/40' : 'bg-success-100 dark:bg-success-900/40'}`}>
                              {hasAnyIssue ? (
                                <ExclamationTriangleIcon className="w-4 h-4 text-error-500" />
                              ) : (
                                <CheckCircleIcon className="w-4 h-4 text-success-500" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Gaeste Section */}
                {participants.filter(p => p.is_guest).length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                      <UserGroupIcon className="w-3.5 h-3.5" />
                      Gaeste ({participants.filter(p => p.is_guest).length})
                    </h4>
                    <div className="space-y-2">
                      {participants.filter(p => p.is_guest).map((participant) => {
                        const { hasTollwutIssue, hasInsuranceIssue } = getParticipantIssues(participant);
                        const displayName = `${participant.vorname} ${participant.nachname || participant.name || ''}`;
                        const hasAnyIssue = hasTollwutIssue || hasInsuranceIssue;

                        return (
                          <div
                            key={participant.member_id || participant.id}
                            className="flex items-center gap-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-700"
                          >
                            <ProfilePicture participant={participant} size="lg" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{displayName}</p>
                                <span className="px-1.5 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded font-medium">
                                  Gast
                                </span>
                              </div>
                              {participant.gebucht_am && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Gebucht: {new Date(participant.gebucht_am).toLocaleDateString('de-DE')}
                                </p>
                              )}
                              {hasAnyIssue && (
                                <div className="flex items-center gap-2 mt-1.5">
                                  {hasTollwutIssue && (
                                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-error-100 dark:bg-error-900/30 rounded">
                                      <BeakerIcon className="w-3 h-3 text-error-600" />
                                      <span className="text-xs font-medium text-error-700 dark:text-error-400">Impfung</span>
                                    </div>
                                  )}
                                  {hasInsuranceIssue && (
                                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-error-100 dark:bg-error-900/30 rounded">
                                      <ShieldCheckIcon className="w-3 h-3 text-error-600" />
                                      <span className="text-xs font-medium text-error-700 dark:text-error-400">Versicherung</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className={`p-2 rounded-full ${hasAnyIssue ? 'bg-error-100 dark:bg-error-900/40' : 'bg-success-100 dark:bg-success-900/40'}`}>
                              {hasAnyIssue ? (
                                <ExclamationTriangleIcon className="w-4 h-4 text-error-500" />
                              ) : (
                                <CheckCircleIcon className="w-4 h-4 text-success-500" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold text-sm rounded-lg transition-colors"
          >
            Schliessen
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParticipantsModal;
